"use strict";
/**
 * Shared cross-boundary type definitions used by both client and server — no
 * runtime behavior beyond enum declarations.
 *
 * These are the canonical shapes for the core domain objects that flow between
 * client and server. Having a single definition here prevents the two sides
 * from drifting. Fields typed as `| null` rather than `| undefined` follow the
 * datastore convention: `null` round-trips through storage as an explicit
 * absence, while `undefined` may be silently dropped by the adapter — the
 * distinction matters for fields you need to clear.
 *
 * This file must not import Node-only or browser-only modules — it is consumed
 * by both the React/Expo client and the Node.js server.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.moderationProfileStatsSchema = exports.PUBLIC_EMAIL_PERMISSION_CATEGORIES = exports.EmailPermissionCategory = exports.moderationTaskBookmarkSchema = exports.moderationTaskSchema = exports.moderationReportSchema = exports.FlagSource = exports.ModTaskType = exports.JudgementType = void 0;
const z = __importStar(require("zod"));
const community_guidelines_1 = require("./community-guidelines");
const comments_1 = require("./orpc/procedures/comments");
const schemas_1 = require("./orpc/schemas");
const types_1 = require("./orpc/types");
// Moderation enums — identical in client and server.
var JudgementType;
(function (JudgementType) {
    JudgementType["REJECT"] = "reject";
    JudgementType["APPROVE"] = "approve";
    JudgementType["BYPASS"] = "bypass";
})(JudgementType || (exports.JudgementType = JudgementType = {}));
var ModTaskType;
(function (ModTaskType) {
    ModTaskType["COMMENT"] = "comment";
})(ModTaskType || (exports.ModTaskType = ModTaskType = {}));
var FlagSource;
(function (FlagSource) {
    FlagSource["REPORT"] = "report";
    FlagSource["PRE_MODERATION"] = "premoderation";
})(FlagSource || (exports.FlagSource = FlagSource = {}));
const flagSourceSchema = z.preprocess((value) => {
    if (!value)
        return undefined;
    return Object.values(FlagSource).includes(value) ? value : FlagSource.PRE_MODERATION;
}, z.enum(FlagSource).optional());
/**
 * One user report that put a moderation task in the queue, as the moderation
 * dashboard needs it: who reported and when. The stored report row also
 * duplicates the task's own author, structure, instance and violations, none
 * of which the dashboard reads, so they stay off the wire.
 *
 * `reporter` is optional only to cover a stored row whose reporter predates
 * the persona shape: reporting always requires a signed-in user, so there is
 * no such thing as a report filed anonymously.
 */
exports.moderationReportSchema = z.object({
    key: z.string().min(1),
    time: z.number(),
    reporter: types_1.userProfileSchema.optional().describe('Profile of the person who filed the report.'),
});
// Client facing schema of moderation tasks
exports.moderationTaskSchema = z.object({
    // Main information
    key: z.string().min(1),
    id: z.string().optional(),
    structureKey: z.string().min(1),
    instanceKey: z.string().min(1),
    type: z.enum(ModTaskType),
    time: z.number(),
    text: z.string().nullable().optional(),
    object: comments_1.commentSchema.optional(),
    objectTime: z.number().optional(),
    author: types_1.userProfileSchema.optional().describe('Profile of the task author.'),
    deleted: z.boolean().optional(),
    // Moderation metadata
    source: flagSourceSchema,
    appealText: z.string().nullable().optional(),
    violations: z.array(community_guidelines_1.moderationRuleSchema).optional(),
    violates: z.boolean().optional(),
    metadata: z
        .object({
        explanation: z.string().optional(),
        blocklistedTerm: z.string().optional(),
    })
        .nullable()
        .optional(),
    receivedWarning: z.boolean().optional(),
    // Judgement metadata
    judgement: z.enum(JudgementType).optional(),
    judgementTime: z.number().optional(),
    humanJudgement: z.boolean().optional(),
    judgeKey: z.string().optional(),
    judge: types_1.userProfileSchema.optional().describe('Profile of the moderator who judged the task, when one has.'),
    judgementViolations: z
        .array(community_guidelines_1.moderationRuleSchema)
        .optional()
        .describe("The guidelines the moderator found broken when rejecting, kept apart from `violations` so the AI's or reporter's " +
        'flags stay comparable with the decision. Empty unless the task was rejected.'),
    customMessage: z.string().nullable().optional(),
    reports: z.array(exports.moderationReportSchema).optional(),
});
exports.moderationTaskBookmarkSchema = z.object({
    modTaskKey: z.string(),
    instanceKey: z.string(),
    structureKey: z.string(),
    createdAt: schemas_1.isoDateTime,
    type: z.enum(ModTaskType),
    isBookmarked: z.boolean(),
});
var EmailPermissionCategory;
(function (EmailPermissionCategory) {
    EmailPermissionCategory["REPLY"] = "reply";
    EmailPermissionCategory["MODERATION_DECISION"] = "moderationDecision";
    EmailPermissionCategory["MODERATOR_TASK_SPACES_CREATED"] = "moderatorTaskSpacesCreated";
    EmailPermissionCategory["MODERATOR_TASK_SPACES_FOLLOWED"] = "moderatorTaskSpacesFollowed";
    EmailPermissionCategory["MODERATOR_TASK_MY_CHANNELS"] = "moderatorTaskMyChannels";
})(EmailPermissionCategory || (exports.EmailPermissionCategory = EmailPermissionCategory = {}));
exports.PUBLIC_EMAIL_PERMISSION_CATEGORIES = [
    EmailPermissionCategory.REPLY,
    EmailPermissionCategory.MODERATION_DECISION,
];
exports.moderationProfileStatsSchema = z.object({
    countFlagsReceived: z.number().nonnegative(),
    countFlagsGiven: z.number().nonnegative(),
    countComments: z.number().nonnegative(),
    countRejectedComments: z.number().nonnegative(),
    memberSince: schemas_1.isoDateTime.nullable(),
});
//# sourceMappingURL=types.js.map